/**
 * Shared resolution-modal primitives (Feb 2026 — Part C).
 *
 * Single source of truth for the dialog shell, save handler, and "Open
 * Contacts" fallback link used by all 7 focused resolution modals.
 * Each modal composes these primitives instead of duplicating shell markup.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Loader2, ExternalLink } from 'lucide-react';
import { tokenStorage } from '../../../lib/tokenStorage';
import { emitDealUpdate } from '../../../hooks/useDealUpdates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Returns getAuthHeaders fn matching the rest of the app's contract. */
const authHeaders = () => ({ Authorization: `Bearer ${tokenStorage.getToken()}` });

/**
 * Single-field resolution modal. Renders title + description + the children
 * (one input + helper text) + Cancel/Save footer. PATCHes /api/deals/{id}
 * with {[fieldKey]: parsedValue} when Save is clicked.
 *
 * Props:
 *   open, onOpenChange         — dialog state
 *   deal                       — current deal object (must have id)
 *   title, description, icon   — header content
 *   fieldKey                   — DB field to PATCH
 *   parse(rawValue)            — returns final payload value (null allowed)
 *   isValid(rawValue)          — gate for Save button
 *   children                   — render-prop receiving ({ value, setValue, submitting })
 *   onResolved                 — called after successful PATCH (parent refetches)
 *   testidPrefix               — data-testid namespace, e.g. "resolve-commission-paid"
 */
export function SingleFieldResolutionModal({
  open, onOpenChange, deal,
  title, description, icon,
  fieldKey, parse, isValid,
  initialValue,
  children,
  onResolved,
  testidPrefix,
}) {
  const [value, setValue] = useState(initialValue ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setValue(initialValue ?? '');
  }, [open, initialValue]);

  const handleSave = async () => {
    if (!deal?.id) return;
    setSubmitting(true);
    try {
      const payload = { [fieldKey]: parse(value) };
      await axios.patch(`${API}/deals/${deal.id}`, payload, { headers: authHeaders() });
      toast.success(`${title} saved`);
      emitDealUpdate({ dealId: deal.id, source: `resolve-${fieldKey}` });
      if (onResolved) onResolved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to save ${title}`);
    } finally {
      setSubmitting(false);
    }
  };

  const canSave = !submitting && (isValid ? isValid(value) : true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4 py-2">
          {children({ value, setValue, submitting })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid={`${testidPrefix}-cancel-btn`}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid={`${testidPrefix}-confirm-btn`}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Link resolution modal — dropdown of filtered contacts with pre-select via
 * default-contact helper. PATCHes /api/deals/{id} with {[fieldKey]: contactId}.
 *
 * Props:
 *   open, onOpenChange, deal
 *   title, description, icon
 *   fieldKey                — DB field to PATCH (company_id|broker_id|funder_id)
 *   contactType             — passed to GET /api/contacts/default-for/{type}
 *                             AND to the per-type contact filter below
 *   contactFilter(c)        — returns boolean for inclusion in dropdown
 *   onResolved
 *   testidPrefix
 */
export function LinkResolutionModal({
  open, onOpenChange, deal,
  title, description, icon,
  fieldKey, contactType, contactFilter,
  onResolved,
  testidPrefix,
}) {
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        // Fetch all contacts then filter client-side (matches existing pattern).
        const r = await axios.get(`${API}/contacts?limit=2000`, { headers: authHeaders() });
        const list = (r.data?.items || r.data || []).filter(contactFilter);
        setContacts(list);

        // Pre-select via the default-contact helper (DC ship endpoint).
        try {
          const d = await axios.get(`${API}/contacts/default-for/${contactType}`, { headers: authHeaders() });
          if (d.data && d.data.id) {
            setSelectedId(d.data.id);
            return;
          }
        } catch (_e) { /* no default — fall through */ }
        setSelectedId('');
      } catch (err) {
        toast.error(`Failed to load ${contactType} contacts`);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, contactType, contactFilter]);

  const handleSave = async () => {
    if (!deal?.id || !selectedId) return;
    setSubmitting(true);
    try {
      await axios.patch(`${API}/deals/${deal.id}`, { [fieldKey]: selectedId }, { headers: authHeaders() });
      toast.success(`${title} saved`);
      emitDealUpdate({ dealId: deal.id, source: `resolve-${fieldKey}` });
      if (onResolved) onResolved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to link ${contactType}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor={`${testidPrefix}-select`}>
              Select {contactType}
            </label>
            <select
              id={`${testidPrefix}-select`}
              data-testid={`${testidPrefix}-select`}
              className="w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={submitting || loading}
            >
              <option value="">
                {loading ? `Loading ${contactType} contacts…` : (contacts.length ? `Select a ${contactType}` : `No ${contactType} contacts found`)}
              </option>
              {contacts.map(c => (
                <option key={c.id} value={c.id} data-testid={`${testidPrefix}-option-${c.id}`}>
                  {c.company_name || c.name}
                </option>
              ))}
            </select>
          </div>
          <a
            href="/contacts"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-xs text-slate-600 hover:text-slate-900 underline decoration-dotted underline-offset-2"
            data-testid={`${testidPrefix}-open-contacts-link`}
          >
            Need to create a new {contactType}? Open Contacts
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid={`${testidPrefix}-cancel-btn`}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedId || submitting} data-testid={`${testidPrefix}-confirm-btn`}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
